import { apiFetch } from './client'
import type { Platform } from './socialAccounts'
import type { PublishSurface } from './media'

/**
 * Fas 2: idé in, ett textutkast per publiceringsyta ut.
 *
 * ⚠️ Casing är inte enhetlig, och det är avsiktligt hos servern.
 * Egenskapsnamn är camelCase (JsonSerializerDefaults.Web), men enum-VÄRDEN är
 * PascalCase eftersom JsonStringEnumConverter registreras utan namngivningspolicy
 * (Program.cs:40, 49). Alltså `{ "severity": "Blocking" }` — aldrig `"blocking"`.
 * Unionerna nedan måste stava medlemsnamnen exakt som C#-enumen.
 */
export type ContentStatus = 'Draft' | 'Approved' | 'Scheduled' | 'Published' | 'Failed'

/** Speglar TextIssueSeverity. Bara två värden finns. */
export type TextIssueSeverity = 'Blocking' | 'Warning'

export interface TextIssue {
  severity: TextIssueSeverity
  message: string
}

export interface GeneratedContentSummary {
  id: string
  surface: PublishSurface
  platform: Platform
  title: string
  body: string
  hashtags: string
  callToAction: string
  status: ContentStatus
  modelUsed: string
  createdAt: string
  updatedAt: string
  /** body + callToAction + hashtags — det plattformen faktiskt publicerar i ett fält. */
  captionLength: number
  captionMaxChars: number
  /**
   * Falskt när texten inte får publiceras. Två skilda skäl ryms här: plattformen
   * avvisar den (för lång), eller vi gör det (obelagt påstående, BrandClaimSpecs).
   * Räknas om vid varje läsning — det finns inget cachat värde att lita fel på.
   */
  canPublish: boolean
  issues: TextIssue[]
}

export interface ContentIdeaSummary {
  id: string
  description: string
  goal: string
  targetAudience: string
  tone: string
  createdAt: string
  drafts: GeneratedContentSummary[]
  /**
   * ⚠️ Fylls BARA av POST /generate. Vid GET är den alltid tom — se ToSummary i
   * ContentEndpoints.cs. Den bär sådant som inte går att härleda ur raderna, t.ex.
   * att en yta helt saknades i Claudes svar. Fångar UI:t den inte i
   * genereringsögonblicket är den borta för gott.
   */
  unresolvedProblems: string[]
}

export interface GenerateContentRequest {
  description: string
  goal?: string
  targetAudience?: string
  tone?: string
  referenceMaterial?: string
  /** Utelämnad = alla fyra ytor. Servern avvisar okända ytor med 400 unsupported_surface. */
  surfaces?: PublishSurface[]
}

export interface UpdateDraftRequest {
  title: string
  body: string
  hashtags: string
  callToAction: string
}

export const contentApi = {
  generate: (req: GenerateContentRequest) =>
    apiFetch<ContentIdeaSummary>('/api/content/generate', { method: 'POST', body: req }),

  listIdeas: () => apiFetch<ContentIdeaSummary[]>('/api/content/ideas'),

  getIdea: (id: string) => apiFetch<ContentIdeaSummary>(`/api/content/ideas/${id}`),

  deleteIdea: (id: string) =>
    apiFetch<void>(`/api/content/ideas/${id}`, { method: 'DELETE' }),

  updateDraft: (id: string, req: UpdateDraftRequest) =>
    apiFetch<GeneratedContentSummary>(`/api/content/drafts/${id}`, { method: 'PUT', body: req }),
}

// ---- presentation ----

/** Ytorna i den ordning servern sorterar dem (OrderBy(c => c.Surface) = enum-ordning). */
export const ALL_SURFACES: readonly PublishSurface[] = [
  'YouTubeVideo',
  'YouTubeShorts',
  'InstagramReels',
  'FacebookReels',
  'TikTok',
]

/** Bara YouTube-ytorna har ett titelfält. Speglar SurfaceTextSpecs.UsesTitle. */
export function usesTitle(surface: PublishSurface): boolean {
  return surface === 'YouTubeVideo' || surface === 'YouTubeShorts'
}

export function hasBlockingIssue(draft: GeneratedContentSummary): boolean {
  return draft.issues.some((i) => i.severity === 'Blocking')
}

/**
 * Speglar SurfaceTextSpecs.Compose: body, uppmaning och hashtaggar publiceras i
 * SAMMA fält hos plattformen, sammanfogade med en blankrad, tomma delar utelämnade.
 *
 * Duplicering av serverlogik, med avsikt. Räknaren måste uppdateras vid varje
 * tangenttryck och kan inte fråga servern. Servern räknar om vid PUT och äger
 * sanningen — den här funktionen får ljuga i millisekunder, aldrig i lagrad data.
 * Ändras Compose på servern måste den ändras här. Det är priset för en levande räknare.
 */
export function composeCaption(body: string, callToAction: string, hashtags: string): string {
  return [body, callToAction, hashtags]
    .filter((s) => s.trim().length > 0)
    .map((s) => s.trim())
    .join('\n\n')
}

/**
 * Servern äger sanningen (canPublish). Här översätts den bara till text + kind.
 * Texten säger alltid samma sak som färgen — WCAG 1.4.1, färg får aldrig vara
 * ensam betydelsebärare.
 */
export function draftStatus(draft: GeneratedContentSummary): {
  text: string
  kind: 'ok' | 'warn' | 'error'
} {
  if (!draft.canPublish) return { text: 'Kan inte publiceras', kind: 'error' }
  if (draft.issues.length > 0) return { text: 'Med anmärkning', kind: 'warn' }
  return { text: 'Klar att publiceras', kind: 'ok' }
}

/**
 * Felkoder från ContentEndpoints + rate limitern, översatta till text ägaren kan
 * agera på. Okända koder faller igenom till anroparens fallback — en tyst
 * standardsträng här hade dolt nya felmoder.
 */
export function contentErrorText(code: string | undefined, status: number): string | null {
  switch (code) {
    case 'anthropic_not_configured':
      return 'Anthropic-nyckeln saknas på servern. Innehållsgenerering är avstängd.'
    case 'description_required':
      return 'Beskriv idén innan du genererar.'
    case 'unsupported_surface':
      return 'En av de valda ytorna stöds inte ännu.'
    case 'content_refused':
      return 'Claude avböjde briefen. Formulera om den och försök igen.'
    case 'content_generation_failed':
      return 'Genereringen misslyckades. Utkasten sparades inte — försök igen.'
    case 'draft_not_editable':
      return 'Utkastet är godkänt eller schemalagt och kan inte längre ändras.'
    case 'rate_limited':
      return 'Du har genererat för många gånger den senaste minuten. Vänta en stund.'
    default:
      return status === 504
        ? 'Genereringen tog för lång tid och avbröts. Utkasten sparades inte.'
        : null
  }
}
